import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Checkbox } from './ui/checkbox';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  BarChart3, TrendingUp, TrendingDown, DollarSign, Loader2, Plus, Trash2,
  Calendar, Mail, Clock, FileText, ArrowUp, ArrowDown, Minus, History, Search,
  Filter,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';


// ── Industry Benchmarks ──
export function IndustryBenchmarks({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reports/industry-benchmarks`, { headers })
      .then(r => setData(r.data)).catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const s = data?.summary || {};
  const ratingColors = { above: 'text-emerald-600', at: 'text-amber-600', below: 'text-red-500' };
  const ratingIcons = { above: ArrowUp, at: Minus, below: ArrowDown };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="industry-benchmarks">
      <div>
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Industry Benchmarks</h2>
        <p className="text-sm text-muted-foreground">How your portfolio compares to similar hospitality technology deployments.</p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="p-4 rounded-lg border bg-card text-center"><div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>{s.above_average || 0}</div><div className="text-[10px] text-muted-foreground">Above Average</div></div>
        <div className="p-4 rounded-lg border bg-card text-center"><div className="text-2xl font-bold text-amber-600" style={{ fontFamily: 'Space Grotesk' }}>{s.at_average || 0}</div><div className="text-[10px] text-muted-foreground">At Average</div></div>
        <div className="p-4 rounded-lg border bg-card text-center"><div className="text-2xl font-bold text-red-500" style={{ fontFamily: 'Space Grotesk' }}>{s.below_average || 0}</div><div className="text-[10px] text-muted-foreground">Below Average</div></div>
      </div>
      <div className="space-y-3">
        {(data?.benchmarks || []).map((b, i) => {
          const Icon = ratingIcons[b.rating] || Minus;
          const pct = b.industry_top > 0 ? Math.min((b.your_value / b.industry_top) * 100, 100) : 50;
          const avgPct = b.industry_top > 0 ? (b.industry_avg / b.industry_top) * 100 : 50;
          return (
            <div key={i} className="p-4 rounded-lg border bg-card">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-semibold">{b.metric}</span>
                <div className={`flex items-center gap-1 text-sm font-bold ${ratingColors[b.rating]}`}><Icon className="h-3.5 w-3.5" /> {b.rating === 'above' ? 'Above' : b.rating === 'at' ? 'At' : 'Below'} Average</div>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground mb-2">
                <span>You: <strong className="text-foreground">{typeof b.your_value === 'number' && b.your_value > 1000 ? `$${b.your_value.toLocaleString()}` : b.your_value}</strong></span>
                <span>Industry Avg: {typeof b.industry_avg === 'number' && b.industry_avg > 1000 ? `$${b.industry_avg.toLocaleString()}` : b.industry_avg}</span>
                <span>Top Performers: {typeof b.industry_top === 'number' && b.industry_top > 1000 ? `$${b.industry_top.toLocaleString()}` : b.industry_top}</span>
              </div>
              <div className="relative h-2 rounded-full bg-muted overflow-hidden">
                <div className="absolute h-full rounded-full bg-blue-500" style={{ width: `${pct}%` }} />
                <div className="absolute h-full w-0.5 bg-amber-500" style={{ left: `${avgPct}%` }} title="Industry Average" />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


// ── Scheduled Reports ──
export function ScheduledReports({ token }) {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ name: '', report_type: 'digest', recipients: '', frequency: 'weekly' });
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try { const r = await axios.get(`${BACKEND_URL}/api/report-schedules`, { headers }); setSchedules(r.data.schedules || []); }
    catch {} finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const REPORT_TYPES = [
    { id: 'digest', label: 'Weekly Digest' }, { id: 'executive', label: 'Executive Summary' },
    { id: 'coverage', label: 'Coverage Matrix' }, { id: 'compliance', label: 'Compliance Report' },
    { id: 'renewals', label: 'Renewal Alerts' }, { id: 'governance', label: 'Governance Report' },
  ];

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/report-schedules`, { ...form, recipients: form.recipients.split(',').map(e => e.trim()).filter(Boolean) }, { headers });
      toast.success('Schedule created');
      setCreateOpen(false);
      setForm({ name: '', report_type: 'digest', recipients: '', frequency: 'weekly' });
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const deleteSchedule = async (id) => {
    try { await axios.delete(`${BACKEND_URL}/api/admin/report-schedules/${id}`, { headers }); toast.success('Deleted'); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  const toggleEnabled = async (id, enabled) => {
    try { await axios.put(`${BACKEND_URL}/api/admin/report-schedules/${id}`, { enabled }, { headers }); fetchAll(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="scheduled-reports">
      <div className="flex items-center justify-between">
        <div><h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Scheduled Reports</h2><p className="text-sm text-muted-foreground">Automated report delivery to stakeholders.</p></div>
        <Button variant="outline" size="sm" onClick={() => setCreateOpen(true)} className="gap-1 text-xs" data-testid="create-schedule-btn"><Plus className="h-3 w-3" /> New Schedule</Button>
      </div>
      {schedules.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground"><Mail className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No scheduled reports yet</p></div>
      ) : (
        <div className="space-y-2">
          {schedules.map(s => (
            <div key={s.id} className="p-4 rounded-lg border bg-card flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2"><span className="font-medium text-sm">{s.name}</span><Badge variant={s.enabled ? 'default' : 'secondary'} className="text-[9px]">{s.enabled ? 'Active' : 'Paused'}</Badge></div>
                <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {s.report_type}</span>
                  <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {s.frequency}</span>
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {(s.recipients || []).length} recipients</span>
                </div>
              </div>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" className="text-xs" onClick={() => toggleEnabled(s.id, !s.enabled)}>{s.enabled ? 'Pause' : 'Enable'}</Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteSchedule(s.id)}><Trash2 className="h-3.5 w-3.5 text-destructive" /></Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md"><DialogHeader><DialogTitle>New Report Schedule</DialogTitle></DialogHeader>
          <div className="space-y-3 py-2">
            <div><label className="text-sm font-medium">Name</label><Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Weekly Executive Digest" className="mt-1" data-testid="schedule-name" /></div>
            <div><label className="text-sm font-medium">Report Type</label>
              <Select value={form.report_type} onValueChange={v => setForm(p => ({ ...p, report_type: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent>{REPORT_TYPES.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}</SelectContent></Select></div>
            <div><label className="text-sm font-medium">Frequency</label>
              <Select value={form.frequency} onValueChange={v => setForm(p => ({ ...p, frequency: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent></Select></div>
            <div><label className="text-sm font-medium">Recipients (comma-separated)</label><Input value={form.recipients} onChange={e => setForm(p => ({ ...p, recipients: e.target.value }))} placeholder="cto@hotel.com, ops@hotel.com" className="mt-1" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving || !form.name || !form.recipients}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Create</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ── ROI Calculator ──
export function RoiCalculator({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ property_id: '', platform_type_id: '', annual_cost: 0, annual_revenue_impact: 0, impact_type: 'revenue', notes: '' });
  const [properties, setProperties] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [saving, setSaving] = useState(false);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const [r, p, pl] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/reports/roi`, { headers }),
        axios.get(`${BACKEND_URL}/api/properties`, { headers }),
        axios.get(`${BACKEND_URL}/api/platform-types`, { headers }),
      ]);
      setData(r.data); setProperties(p.data || []); setPlatforms(pl.data || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [headers]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const save = async () => {
    setSaving(true);
    try { await axios.put(`${BACKEND_URL}/api/admin/roi`, form, { headers }); toast.success('ROI data saved'); setAddOpen(false); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const s = data?.summary || {};

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="roi-calculator">
      <div className="flex items-center justify-between">
        <div><h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>ROI Calculator</h2><p className="text-sm text-muted-foreground">Track return on investment per platform across properties.</p></div>
        <Button variant="outline" size="sm" onClick={() => setAddOpen(true)} className="gap-1 text-xs" data-testid="add-roi-btn"><Plus className="h-3 w-3" /> Add ROI Data</Button>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk', color: s.overall_roi_pct >= 0 ? '#16a34a' : '#dc2626' }}>{s.overall_roi_pct || 0}%</div><div className="text-[10px] text-muted-foreground">Overall ROI</div></div>
        <div className="p-3 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>${(s.total_cost || 0).toLocaleString()}</div><div className="text-[10px] text-muted-foreground">Total Cost</div></div>
        <div className="p-3 rounded-lg border bg-card text-center"><div className="text-2xl font-bold text-emerald-600" style={{ fontFamily: 'Space Grotesk' }}>${(s.total_impact || 0).toLocaleString()}</div><div className="text-[10px] text-muted-foreground">Total Impact</div></div>
        <div className="p-3 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk', color: s.net_return >= 0 ? '#16a34a' : '#dc2626' }}>${(s.net_return || 0).toLocaleString()}</div><div className="text-[10px] text-muted-foreground">Net Return</div></div>
      </div>
      <div className="border rounded-lg overflow-hidden">
        <table className="w-full text-sm"><thead><tr className="bg-muted/50"><th className="p-2.5 text-left text-xs font-semibold">Property</th><th className="p-2.5 text-left text-xs font-semibold">Platform</th><th className="p-2.5 text-center text-xs font-semibold">Annual Cost</th><th className="p-2.5 text-center text-xs font-semibold">Annual Impact</th><th className="p-2.5 text-center text-xs font-semibold">ROI %</th><th className="p-2.5 text-center text-xs font-semibold">Type</th></tr></thead>
          <tbody>{(data?.records || []).map((r, i) => (
            <tr key={i} className="border-t hover:bg-muted/20"><td className="p-2.5 font-medium">{r.property_name}</td><td className="p-2.5">{r.platform}</td><td className="p-2.5 text-center font-mono text-xs">${r.annual_cost.toLocaleString()}</td><td className="p-2.5 text-center font-mono text-xs text-emerald-600">${r.annual_impact.toLocaleString()}</td><td className="p-2.5 text-center"><span className={`font-bold text-xs ${r.roi_pct >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{r.roi_pct}%</span></td><td className="p-2.5 text-center"><Badge variant="outline" className="text-[9px] capitalize">{r.impact_type}</Badge></td></tr>
          ))}{(data?.records || []).length === 0 && <tr><td colSpan={6} className="p-8 text-center text-sm text-muted-foreground">No ROI data tracked yet.</td></tr>}</tbody>
        </table>
      </div>
      <Dialog open={addOpen} onOpenChange={setAddOpen}><DialogContent className="max-w-md"><DialogHeader><DialogTitle>Add ROI Data</DialogTitle></DialogHeader><div className="space-y-3 py-2">
        <div><label className="text-sm font-medium">Property</label><Select value={form.property_id} onValueChange={v => setForm(p => ({ ...p, property_id: v }))}><SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{properties.filter(p => p.id !== 'corporate').map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
        <div><label className="text-sm font-medium">Platform</label><Select value={form.platform_type_id} onValueChange={v => setForm(p => ({ ...p, platform_type_id: v }))}><SelectTrigger className="mt-1"><SelectValue placeholder="Select..." /></SelectTrigger><SelectContent>{platforms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent></Select></div>
        <div className="grid grid-cols-2 gap-3"><div><label className="text-xs font-medium">Annual Cost ($)</label><Input type="number" value={form.annual_cost} onChange={e => setForm(p => ({ ...p, annual_cost: parseFloat(e.target.value) || 0 }))} className="mt-1 h-8 text-sm" /></div><div><label className="text-xs font-medium">Annual Impact ($)</label><Input type="number" value={form.annual_revenue_impact} onChange={e => setForm(p => ({ ...p, annual_revenue_impact: parseFloat(e.target.value) || 0 }))} className="mt-1 h-8 text-sm" /></div></div>
        <div><label className="text-sm font-medium">Impact Type</label><Select value={form.impact_type} onValueChange={v => setForm(p => ({ ...p, impact_type: v }))}><SelectTrigger className="mt-1"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="revenue">Revenue Generated</SelectItem><SelectItem value="savings">Cost Savings</SelectItem><SelectItem value="efficiency">Efficiency Gains</SelectItem></SelectContent></Select></div>
        <div><label className="text-sm font-medium">Notes</label><Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Upselling platform generated $80k in incremental revenue" className="mt-1" /></div>
      </div><DialogFooter><Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button><Button onClick={save} disabled={saving || !form.property_id || !form.platform_type_id}>{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Save</Button></DialogFooter></DialogContent></Dialog>
    </div>
  );
}


// ── Vendor Spend Analytics ──
export function SpendAnalytics({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/reports/spend-analytics`, { headers })
      .then(r => setData(r.data)).catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, [headers]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const s = data?.summary || {};

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="spend-analytics">
      <div><h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Vendor Spend Analytics</h2><p className="text-sm text-muted-foreground">Analyze spend distribution across vendors, platforms, and properties.</p></div>
      <div className="grid grid-cols-3 gap-3">
        <div className="p-4 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>${(s.total_spend || 0).toLocaleString()}</div><div className="text-[10px] text-muted-foreground">Total Annual Spend</div></div>
        <div className="p-4 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{s.vendor_count || 0}</div><div className="text-[10px] text-muted-foreground">Vendors with Contracts</div></div>
        <div className="p-4 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{s.contract_count || 0}</div><div className="text-[10px] text-muted-foreground">Total Contracts</div></div>
      </div>

      {(data?.by_vendor || []).length > 0 && (<div>
        <h3 className="text-sm font-semibold mb-3">Spend by Vendor</h3>
        <div className="space-y-2">{(data?.by_vendor || []).slice(0, 10).map((v, i) => (
          <div key={i} className="flex items-center gap-3 text-sm">
            <span className="w-40 truncate font-medium">{v.vendor}</span>
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-blue-500" style={{ width: `${v.pct_of_total}%` }} /></div>
            <span className="font-mono text-xs w-24 text-right">${v.spend.toLocaleString()}</span>
            <span className="text-[10px] text-muted-foreground w-10 text-right">{v.pct_of_total}%</span>
          </div>
        ))}</div>
      </div>)}

      {(data?.by_platform || []).length > 0 && (<div>
        <h3 className="text-sm font-semibold mb-3">Spend by Platform Category</h3>
        <div className="space-y-2">{(data?.by_platform || []).slice(0, 10).map((p, i) => (
          <div key={i} className="flex items-center gap-3 text-sm">
            <span className="w-40 truncate">{p.platform}</span>
            <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full rounded-full bg-emerald-500" style={{ width: `${p.pct}%` }} /></div>
            <span className="font-mono text-xs w-24 text-right">${p.spend.toLocaleString()}</span>
          </div>
        ))}</div>
      </div>)}

      {(data?.by_vendor || []).length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No contract spend data tracked yet. Add contracts to see spend analytics.</p>}
    </div>
  );
}


// ── Changelog / Audit Trail ──
export function ChangelogViewer({ token }) {
  const [entries, setEntries] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetchAll = useCallback(async () => {
    try {
      const params = {};
      if (filterType) params.entity_type = filterType;
      const [e, s] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/changelog`, { headers, params: { ...params, limit: 50 } }),
        axios.get(`${BACKEND_URL}/api/changelog/summary`, { headers }),
      ]);
      setEntries(e.data.entries || []);
      setSummary(s.data);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [headers, filterType]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>;

  const actionColors = { create: 'bg-emerald-100 text-emerald-700', update: 'bg-blue-100 text-blue-700', delete: 'bg-red-100 text-red-700' };
  const sm = summary || {};

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="changelog">
      <div className="flex items-center justify-between">
        <div><h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Changelog & Audit Trail</h2><p className="text-sm text-muted-foreground">Full history of every data change in the platform.</p></div>
        <Select value={filterType || '__all__'} onValueChange={v => { setFilterType(v === '__all__' ? '' : v); setLoading(true); }}>
          <SelectTrigger className="w-44 h-8 text-xs"><SelectValue placeholder="All types" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All Types</SelectItem>
            {Object.keys(sm.by_entity_type || {}).map(t => <SelectItem key={t} value={t}>{t} ({sm.by_entity_type[t]})</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="p-3 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{sm.total_changes || 0}</div><div className="text-[10px] text-muted-foreground">Changes ({sm.days || 30}d)</div></div>
        {Object.entries(sm.by_action || {}).slice(0, 3).map(([action, count]) => (
          <div key={action} className="p-3 rounded-lg border bg-card text-center"><div className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk' }}>{count}</div><div className="text-[10px] text-muted-foreground capitalize">{action}s</div></div>
        ))}
      </div>
      <div className="space-y-1">
        {entries.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground"><History className="h-10 w-10 mx-auto mb-3 opacity-30" /><p className="text-sm">No changes recorded yet</p><p className="text-xs mt-1">Changes will appear here as data is created, updated, or deleted</p></div>
        ) : entries.map((e, i) => (
          <div key={i} className="flex items-center gap-3 p-2.5 rounded-lg border bg-card hover:bg-muted/20 text-sm">
            <Badge className={`text-[9px] shrink-0 ${actionColors[e.action] || ''}`}>{e.action}</Badge>
            <span className="font-medium w-24 truncate">{e.entity_type}</span>
            <span className="text-muted-foreground text-xs flex-1 truncate">{JSON.stringify(e.changes).slice(0, 100)}</span>
            <span className="text-[10px] text-muted-foreground shrink-0">{e.user_name || e.user_id || 'system'}</span>
            <span className="text-[10px] text-muted-foreground shrink-0">{e.timestamp ? new Date(e.timestamp).toLocaleDateString() : ''}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
